import React, { useState, useEffect } from 'react';
import './AuthForms.css';

const ResetPassword = ({ setView }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const tokenParam = params.get('token');
    setEmail(emailParam || '');
    setToken(tokenParam || '');
    if (!emailParam || !tokenParam) {
      setError('Invalid or missing reset link. Please request a new reset link.');
    }

    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok) {
          setCsrfToken(data.csrfToken);
        } else {
          setError('Failed to initialize password reset. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching CSRF token:', err);
        setError('Failed to initialize password reset. Please try again.');
      }
    };
    fetchCsrfToken();
  }, []);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    console.log('Attempting password reset with password:', newPassword); // Debug log

    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required.');
      console.log('Password reset failed: Missing password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      console.log('Password reset failed: Passwords do not match');
      return;
    }

    // Static regex with all special characters properly escaped, including underscore
    const specialChars = '@$!%*?&,+=[\\{\\}\\|;:\'".<>()_-';
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&,+=\[\]\{\}\|\;:'"\.<>\(\)_-])[A-Za-z\d@$!%*?&,+=\[\]\{\}\|\;:'"\.<>\(\)_-]{8,}$/;
    try {
      console.log('Validating password with regex:', passwordRegex.source); // Debug log
      if (!passwordRegex.test(newPassword)) {
        setError(`Password must be at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character (one of: ${specialChars}).`);
        console.log('Password reset failed: Invalid password format', newPassword);
        return;
      }
    } catch (regexError) {
      console.error('Regex validation error:', regexError);
      setError('Invalid password format. Please ensure your password meets the requirements.');
      console.log('Password reset failed: Regex error', newPassword);
      return;
    }

    if (!csrfToken) {
      setError('CSRF token not available. Please try again.');
      console.log('Password reset failed: Missing CSRF token');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ email, token, newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage(data.message || 'Password reset successful!');
        setCsrfToken(data.csrfToken || '');
        localStorage.clear();
        sessionStorage.clear();
        console.log('Password reset successful for email:', email);
        setTimeout(() => {
          setView('login');
          window.location.href = '/login'; // Fallback navigation
        }, 3000);
      } else {
        if (data.message && data.message.toLowerCase().includes('compromised')) {
          setError('This password pattern is commonly used. Please choose a stronger, unique password.');
          console.log('Password reset failed: Compromised password detected');
        } else {
          setError(data.message || 'Error resetting password.');
          console.log('Password reset failed:', data.message);
        }
        setCsrfToken(data.csrfToken || '');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Server error. Please try again later.');
      console.log('Password reset failed: Server error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-forms">
        <div className="auth-forms-inner">
          <h2>Reset Your Password</h2>
          <form id="reset-password-form" onSubmit={handleResetPassword}>
            <div className="form-group password-group">
              <label>New Password *</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                />
                <button type="button" onClick={togglePasswordVisibility} className="view-password">
                  {showPassword ? (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 7l-10 10M7 7l10 10M12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7z"></path>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="form-group password-group">
              <label>Confirm Password *</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                />
                <button type="button" onClick={togglePasswordVisibility} className="view-password">
                  {showPassword ? (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 7l-10 10M7 7l10 10M12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7z"></path>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="auth-button-group">
              <button type="submit" disabled={loading || !csrfToken} className="login-button">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
              <button type="button" onClick={() => setView('login')} className="back-button" disabled={loading}>
                Cancel
              </button>
            </div>
            {error && <p className="error">{error}</p>}
            {message && <p className="success">{message}</p>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;